import format from 'date-fns/format';
import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  GetProfileIncludeOptions,
  ProfileAttributes,
} from '@loyalty/shared-common/profile-api-client';
import { PromocodeAttributes } from '@loyalty/shared-common/rewards-api-client';
import { colorPalette, useAlerts } from '@qualifio/ui-commons';
import { extractPayloadFromToken } from '@loyalty/shared-common';
import { faTrophy } from '@fortawesome/pro-solid-svg-icons/faTrophy';
import { useTranslation } from 'react-i18next';
import { ProfileDisplayName } from '../../../profiles/utils/ProfileDisplayName';
import { useApi } from '../../../shared/api-client/ApiClientProvider';
import { useProgram } from '../../../loyalty/ProgramContext';

function PrettyDate({ date }: { date: Date | string | undefined | null }) {
  if (!date) {
    return <></>;
  }
  const dateObject = date instanceof Date ? date : new Date(date);
  return (
    <>
      <b>{format(dateObject, 'yyyy-MM-dd')}</b> - {format(dateObject, 'HH:mm')}
    </>
  );
}

function Profile({ profileId }: { profileId: string }) {
  const { program } = useProgram();
  const { notify } = useAlerts();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<ProfileAttributes>();

  const { profileApi } = useApi();

  useEffect(() => {
    setLoading(true);
    profileApi
      .getProfile(profileId, {
        params: { programId: program.id, include: [GetProfileIncludeOptions.memberships] },
      })
      .then(setProfile)
      .catch(error => {
        if (error?.response?.status !== 404) {
          notify(t('rewards.promo-codes.errors.profile'), 'error');
        }
      })
      .finally(() => setLoading(false));
  }, [profileId]);

  return loading ? (
    <CircularProgress />
  ) : profile?.memberships && profile.memberships.length > 0 ? (
    <Box sx={{ fontSize: 14 }}>
      <b>
        <ProfileDisplayName profile={profile} />
      </b>{' '}
      - <a href={`mailto:${profile.email}`}>{profile.email}</a>
    </Box>
  ) : (
    <>{t('rewards.promo-codes.errors.no-profile')}</>
  );
}

export function CampaignRedeemerListItem({
  promocode,
}: {
  promocode: PromocodeAttributes;
}): React.ReactElement {
  const { t } = useTranslation();

  const isWon = !!promocode.profileId;
  const token = Object.values(JSON.parse(promocode.code))[0] as string;
  const tokenPayload = extractPayloadFromToken(token);

  const expirationDate = new Date(parseInt(tokenPayload.exp) * 1000);

  return (
    <ListItem
      component={Paper}
      sx={{
        alignItems: 'center',
        backgroundColor: !isWon ? colorPalette.grey.light : 'white',
        borderLeft: `4px solid ${isWon ? colorPalette.orange.main : colorPalette.blue.main}`,
        display: 'flex',
        marginBottom: 1,
        overflow: 'hidden',
        paddingBottom: 0,
        paddingLeft: 0,
        paddingTop: 0,
        '& a': {
          color: colorPalette.blue.main,
          textDecoration: 'none !important',
        },
      }}
    >
      <ListItemText
        sx={{
          alignItems: 'center',
          borderRight: `1px solid ${colorPalette.grey.main}`,
          display: 'flex',
          margin: 0,
          maxWidth: 230,
          minHeight: 80,
          padding: 2,
        }}
        primary={
          <Box component="span" sx={{ fontSize: 14 }}>
            <PrettyDate date={promocode.acquiredAt} />
          </Box>
        }
        primaryTypographyProps={{
          sx: {
            color: isWon ? colorPalette.grey.main : undefined,
            fontWeight: 700,
          },
        }}
      />
      {promocode.profileId && (
        <ListItemText
          sx={{ margin: 0, padding: 1 }}
          primary={<Profile profileId={promocode.profileId} />}
          secondary={
            <Box component="span" sx={{ fontSize: 14, display: 'flex', gap: '5px' }}>
              <span> {t('rewards.campaign-redeemer.token.expiration')} </span>
              <PrettyDate date={expirationDate} />
            </Box>
          }
        />
      )}
      <ListItemSecondaryAction>
        <Box
          component={FontAwesomeIcon}
          sx={{
            color: isWon ? colorPalette.orange.main : colorPalette.grey.dark,
            fontSize: 16,
          }}
          icon={faTrophy}
        />
      </ListItemSecondaryAction>
    </ListItem>
  );
}
